import React, { useState, useEffect } from 'react';
import { Modal, Input, Button, Typography, Space, Alert, message, Checkbox, Row, Col, Select } from 'antd';
import { MailOutlined, SendOutlined, PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import { getFunctions, httpsCallable } from 'firebase/functions';

const { Text, Title } = Typography;
const { TextArea } = Input;
const { Option } = Select;

const EmailModal = ({ 
  visible, 
  onCancel, 
  documentData, 
  documentType, // 'invoice' or 'quotation'
  onSendSuccess 
}) => {
  const [recipients, setRecipients] = useState([]);
  const [subject, setSubject] = useState('');
  const [customMessage, setCustomMessage] = useState('');
  const [useDefaultMessage, setUseDefaultMessage] = useState(true);
  const [attachPDF, setAttachPDF] = useState(true);
  const [sending, setSending] = useState(false);

  // Initialize form when modal opens
  useEffect(() => {
    if (visible && documentData) {
      // Set primary recipient
      const primaryEmail = documentData.email || '';
      setRecipients([{ email: primaryEmail, type: 'to' }]);
      
      // Set default subject
      const docNumber = documentType === 'invoice' 
        ? documentData.invoiceNumber 
        : documentData.quotationNumber;
      setSubject(`${documentType === 'invoice' ? 'Invoice' : 'Quotation'} ${docNumber} from Beema Scaffolding`);
      
      // Set default message as editable content
      setCustomMessage(getDefaultMessage());
      setUseDefaultMessage(true);
      setAttachPDF(true);
    }
  }, [visible, documentData, documentType]);

  // Generate default message
  const getDefaultMessage = () => {
    const docNumber = documentType === 'invoice' 
      ? documentData?.invoiceNumber 
      : documentData?.quotationNumber;
    
    const contactName = documentData?.attn || 'there';
    const docName = documentType === 'invoice' ? 'invoice' : 'quotation';
    const companyName = documentData?.companyName || '';
    
    return `Dear ${contactName},

I hope this email finds you well.

Please find attached your ${docName} ${docNumber}${companyName ? ` for ${companyName}` : ''}.

If you have any questions or require any clarification, please don't hesitate to contact us.

Thank you for your business.

Best regards,
Beema Scaffolding Team

---
Beema Scaffolding Pte Ltd
Email: contact@beemascaffolding.sg
Website: www.beemascaffolding.sg`;
  };

  // Add new recipient
  const addRecipient = () => {
    setRecipients([...recipients, { email: '', type: 'cc' }]);
  };

  // Remove recipient
  const removeRecipient = (index) => {
    if (recipients.length > 1) {
      const newRecipients = recipients.filter((_, i) => i !== index);
      setRecipients(newRecipients);
    }
  };

  // Update recipient email
  const updateRecipientEmail = (index, email) => {
    const newRecipients = [...recipients];
    newRecipients[index].email = email;
    setRecipients(newRecipients);
  };

  // Update recipient type
  const updateRecipientType = (index, type) => {
    const newRecipients = [...recipients];
    newRecipients[index].type = type;
    setRecipients(newRecipients);
  };

  // Validate email format
  const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Validate form
  const validateForm = () => {
    // Check if at least one recipient has a valid email
    const validRecipients = recipients.filter(r => r.email && isValidEmail(r.email));
    if (validRecipients.length === 0) {
      message.error('Please provide at least one valid email address');
      return false;
    }

    // Check subject
    if (!subject.trim()) {
      message.error('Please provide a subject line');
      return false;
    }

    return true;
  };

  const handleSend = async () => {
    if (!validateForm()) {
      return;
    }

    setSending(true);
    
    try {
      const functions = getFunctions();
      const sendEmail = httpsCallable(functions, 'sendEmail');
      
      const messageText = customMessage.trim();
      
      // Prepare recipients object
      const emailRecipients = {
        to: recipients.filter(r => r.type === 'to' && r.email && isValidEmail(r.email)).map(r => r.email),
        cc: recipients.filter(r => r.type === 'cc' && r.email && isValidEmail(r.email)).map(r => r.email),
        bcc: recipients.filter(r => r.type === 'bcc' && r.email && isValidEmail(r.email)).map(r => r.email)
      };

      const result = await sendEmail({
        documentType: documentType,
        documentId: documentData.id,
        recipients: emailRecipients,
        subject: subject.trim(),
        message: messageText,
        attachPDF: attachPDF
      });

      console.log('Email sent successfully:', result.data);
      
      message.success(`${documentType} sent via email successfully!`);
      
      // Call success callback to refresh data
      if (onSendSuccess) {
        onSendSuccess();
      }
      
      // Close modal
      onCancel();
      
    } catch (error) {
      console.error('Error sending email:', error);
      
      let errorMessage = 'Failed to send email';
      
      if (error.code === 'functions/invalid-argument') {
        errorMessage = 'Invalid email addresses or document data';
      } else if (error.code === 'functions/not-found') {
        errorMessage = 'Document not found';
      } else if (error.code === 'functions/unauthenticated') {
        errorMessage = 'Authentication required';
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      message.error(errorMessage);
    } finally {
      setSending(false);
    }
  };

  const handleCancel = () => {
    setRecipients([]);
    setSubject('');
    setCustomMessage('');
    setUseDefaultMessage(true);
    setAttachPDF(true);
    onCancel();
  };

  if (!documentData) {
    return null;
  }

  const docNumber = documentType === 'invoice' 
    ? documentData.invoiceNumber 
    : documentData.quotationNumber;

  return (
    <Modal
      title={
        <Space>
          <MailOutlined style={{ color: '#1890ff' }} />
          <span>Send via Email</span>
        </Space>
      }
      open={visible}
      onCancel={handleCancel}
      width={700}
      footer={[
        <Button key="cancel" onClick={handleCancel} disabled={sending}>
          Cancel
        </Button>,
        <Button
          key="send"
          type="primary"
          icon={<SendOutlined />}
          onClick={handleSend}
          loading={sending}
        >
          Send Email
        </Button>
      ]}
    >
      <div style={{ padding: '16px 0' }}>
        {/* Recipients Section */}
        <div style={{ marginBottom: 16 }}>
          <Title level={5}>Recipients:</Title>
          {recipients.map((recipient, index) => (
            <Row key={index} gutter={8} style={{ marginBottom: 8 }}>
              <Col span={4}>
                <Select
                  value={recipient.type}
                  onChange={(value) => updateRecipientType(index, value)}
                  style={{ width: '100%' }}
                >
                  <Option value="to">TO</Option>
                  <Option value="cc">CC</Option>
                  <Option value="bcc">BCC</Option>
                </Select>
              </Col>
              <Col span={16}>
                <Input
                  value={recipient.email}
                  onChange={(e) => updateRecipientEmail(index, e.target.value)}
                  placeholder="Enter email address"
                  status={recipient.email && !isValidEmail(recipient.email) ? 'error' : ''}
                />
              </Col>
              <Col span={4}>
                <Space>
                  {index === recipients.length - 1 && (
                    <Button
                      type="text"
                      icon={<PlusOutlined />}
                      onClick={addRecipient}
                      size="small"
                    />
                  )}
                  {recipients.length > 1 && (
                    <Button
                      type="text"
                      danger
                      icon={<DeleteOutlined />}
                      onClick={() => removeRecipient(index)}
                      size="small"
                    />
                  )}
                </Space>
              </Col>
            </Row>
          ))}
        </div>

        {/* Document Info */}
        <div style={{ marginBottom: 16 }}>
          <Text strong>Document: </Text>
          <Text>
            {documentType === 'invoice' ? 'Invoice' : 'Quotation'} {docNumber}
          </Text>
          <br />
          <Text type="secondary">
            {documentData.companyName} - {documentData.attn}
          </Text>
        </div>

        {/* Subject */}
        <div style={{ marginBottom: 16 }}>
          <Title level={5}>Subject:</Title>
          <Input
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            placeholder="Email subject"
            maxLength={200}
          />
        </div>

        {/* PDF Attachment Option */}
        <div style={{ marginBottom: 16 }}>
          <Checkbox
            checked={attachPDF}
            onChange={(e) => setAttachPDF(e.target.checked)}
          >
            Attach PDF document
          </Checkbox>
        </div>

        {/* Message */}
        <div style={{ marginBottom: 16 }}>
          <Title level={5}>Message:</Title>
          <div style={{ marginBottom: 8 }}>
            <Button
              type={useDefaultMessage ? "primary" : "default"}
              size="small"
              onClick={() => {
                setCustomMessage(getDefaultMessage());
                setUseDefaultMessage(true);
              }}
              style={{ marginRight: 8 }}
            >
              Use Default Template
            </Button>
            <Button
              type={!useDefaultMessage ? "primary" : "default"}
              size="small"
              onClick={() => {
                setCustomMessage('');
                setUseDefaultMessage(false);
              }}
            >
              Start Fresh
            </Button>
          </div>
          <TextArea
            value={customMessage}
            onChange={(e) => {
              setCustomMessage(e.target.value);
              setUseDefaultMessage(false);
            }}
            placeholder="Enter your email message here..."
            rows={10}
            maxLength={2000}
            showCount
          />
          <Text type="secondary" style={{ fontSize: '12px', marginTop: '4px' }}>
            You can edit the default message template or write your own custom message
          </Text>
        </div>

        {/* Validation Alerts */}
        {recipients.some(r => r.email && !isValidEmail(r.email)) && (
          <Alert
            message="Invalid email addresses"
            description="Please check the email addresses marked in red."
            type="warning"
            showIcon
            style={{ marginBottom: 16 }}
          />
        )}

        {recipients.filter(r => r.email && isValidEmail(r.email)).length === 0 && (
          <Alert
            message="No valid recipients"
            description="Please provide at least one valid email address."
            type="error"
            showIcon
            style={{ marginBottom: 16 }}
          />
        )}

        {/* Preview */}
        <div style={{ marginTop: 16, padding: 12, backgroundColor: '#f5f5f5', borderRadius: 6 }}>
          <Text strong>Preview:</Text>
          <div style={{ marginTop: 8 }}>
            <Text strong>To: </Text>
            <Text>{recipients.filter(r => r.type === 'to' && r.email).map(r => r.email).join(', ') || 'No recipients'}</Text>
          </div>
          {recipients.filter(r => r.type === 'cc' && r.email).length > 0 && (
            <div>
              <Text strong>CC: </Text>
              <Text>{recipients.filter(r => r.type === 'cc' && r.email).map(r => r.email).join(', ')}</Text>
            </div>
          )}
          {recipients.filter(r => r.type === 'bcc' && r.email).length > 0 && (
            <div>
              <Text strong>BCC: </Text>
              <Text>{recipients.filter(r => r.type === 'bcc' && r.email).map(r => r.email).join(', ')}</Text>
            </div>
          )}
          <div>
            <Text strong>Subject: </Text>
            <Text>{subject || 'No subject'}</Text>
          </div>
          {attachPDF && (
            <div>
              <Text strong>Attachment: </Text>
              <Text>{docNumber}.pdf</Text>
            </div>
          )}
          <div style={{ 
            marginTop: 8, 
            padding: 8, 
            backgroundColor: 'white', 
            borderRadius: 4,
            fontFamily: 'monospace',
            fontSize: '12px',
            maxHeight: '150px',
            overflow: 'auto'
          }}>
            {customMessage.trim() || 'No message content'}
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default EmailModal;
